import Vue from "vue";
import merge from "lodash/merge";
import VueI18n from "vue-i18n";
import appConfig from "../../appconfig";
import * as mutationTypes from "../../store/mutation-types";
import {
	getTenantLocalizations,
	getTenantGroupLocalizations
} from "./LocalizationProvider";
import { fetchComponentTranslations } from "./ContentProvider";

const tenantId = appConfig.tenantId;
const tenantLocale = appConfig.tenantConfig.locale;

Vue.use(VueI18n);
const i18n = new VueI18n({
	locale: tenantLocale,
	fallbackLocale: tenantLocale,
	messages: {},
	missing: (_, key) => {
		if(!window.location.href.includes("/admin")) {
			return "";
		}
		const x = (key || "").split(".");
		return `!${x[x.length - 1]}!`;
	}
});
export default i18n;

export const i18nInitializer = async function (store, isAdmin = false) {
	const params = new URLSearchParams(window.location.href);
	const lastLocaleUsed =
		(isAdmin ? store.getters.lastLocaleAdmin : null)
		|| params.get("languageCode") // for browser channels, we set new tenant language via url query params
		|| window.localStorage.getItem("languageCode") // for mobile app, we set new tenant language via local storage
		|| store.getters.lastLocale // if no language code is detected through above, we check the vuex getter
		|| tenantLocale; // fallback to default tenant language code

	// clear the languageCode from localStorage after the inital switch from one tenant to the other so that we are not stuck
	// with that original language but we can rely on the one saved in Vuex instead.
	window.localStorage.removeItem("languageCode");

	const useApiCdnCaching = store.getters.featureToggles.useApiCdnCaching;
 
	const localesPromise = async () => {
		const tenantGroup = appConfig.tenantConfig.tenantGroupSystemName;
		if (tenantGroup && !tenantGroup.toLowerCase().includes("sandbox")) {
			return getTenantGroupLocalizations(tenantGroup, useApiCdnCaching);
		}
		return getTenantLocalizations(useApiCdnCaching);
	};

	// if locale is NOT supported in the tenant, replace that with tenantLocale

	// TODO: remove these defaultLocales, it's a hack to allow backwards compatibility for the current deployment
	// as the endpoint being called is not yet deployed in the backend.
	const defaultLocales = [
		{ languageCode: "da", language: "Dansk", tenantId: 100 },
		{ languageCode: "da", language: "Dansk", tenantId: 101 },
		{ languageCode: "da", language: "Dansk", tenantId: 102 },
		{ languageCode: "da", language: "Dansk", tenantId: 103 }
	];
	const allTenantLanguages = await localesPromise().then(result => Array.isArray(result) ? result : defaultLocales).catch(() => defaultLocales);
	const tenantSpecificLanguages = allTenantLanguages.filter(locale => locale.tenantId === tenantId);
	const tenantSpecificLanguageCodes = tenantSpecificLanguages.map(locale => locale.languageCode);
	const localeToUse = !tenantSpecificLanguageCodes.includes(lastLocaleUsed) ? tenantLocale : lastLocaleUsed;
	store.commit(mutationTypes.SET_LAST_LOCALE, localeToUse);
	store.commit(mutationTypes.SET_LAST_LOCALE_ADMIN, tenantLocale);

	const { staticText, catalog } = await fetchComponentTranslations(
		store,
		localeToUse,
		isAdmin,
		tenantId
	);

	store.commit(mutationTypes.CLEAR_LOCALIZATION_CACHE);
	store.commit(mutationTypes.ADD_CATALOG, [localeToUse, staticText]);
	store.commit(mutationTypes.SET_DYNAMIC_CATALOG, [localeToUse, catalog]);

	const combinedCatalogs = merge(staticText, catalog);

	i18n.locale = localeToUse;
	i18n.setLocaleMessage(localeToUse, combinedCatalogs);
	store.commit(mutationTypes.ALL_TENANT_LANGUAGES, allTenantLanguages);
	store.commit(mutationTypes.ADD_LANGUAGES, tenantSpecificLanguages);

	if (!appConfig.mobile.isMobileApp()) {
		const currentUrl = new URL(window.location.href);
		const currentUrlSearchParams = new URLSearchParams(currentUrl.searchParams);
		currentUrlSearchParams.delete("languageCode");
		currentUrl.search = new URLSearchParams(currentUrlSearchParams);
		window.history.pushState("", "", currentUrl.toString());
	}
};
